<template>
  <div
    class="flex flex-row"
    v-click-outside="onClickOutside"
    :class="{ 'min-h-screen': $mq != 'mobile' }"
  >
    <div class="relative z-100 w-full">
      <div v-if="!static" class="h-full">
        <!-- Tablet Above: a rail of even 80px items -->
        <mq-layout
          mq="tablet+"
          class="flex flex-col items-center h-full w-24 bg-grey-darkest"
        >
          <!-- Same height as the page header (h-24 md:h-32) so the two blocks line up -->
          <a href="/#/vaults" class="h-24 md:h-32 w-full px-2 flex items-center justify-center">
            <img
              :src="'/images/sidenav/' + vault_type + '-sidelogo.png'"
              class="max-w-full"
              alt
            />
          </a>

          <div v-if="isAuthenticated" class="w-full flex-1 flex flex-col text-center">
            <!-- current vault, click to switch -->
            <a
              href="#"
              v-if="inVault && getCurrentVault && getCurrentVault.name"
              class="block w-full px-1 py-3 border-t border-grey-dark no-underline hover:bg-black"
              :class="{ 'bg-black': toggle_vaults }"
              :title="getCurrentVault.name"
              @click.prevent="toggleVaults()"
            >
              <p class="text-xxs uppercase tracking-wide text-grey whitespace-no-wrap">Vault</p>
              <!-- full name, wrapped to three lines; the title shows the rest when it is longer -->
              <p class="flex items-start justify-center text-white text-xs font-medium leading-tight">
                <span class="sidenav-vault-name min-w-0 break-words">{{ getCurrentVault.name }}</span>
                <i class="fa fa-chevron-right ml-1 flex-none text-grey text-xxs"></i>
              </p>
            </a>

            <!-- vault list -->
            <div class="w-full border-t border-grey-dark relative">
              <router-link
                :to="{ name: 'vaults' }"
                :class="[navItemClass, { 'bg-black border-brand': vaults_current_active || toggle_vaults }]"
                @click.native="closeAllSubMenu()"
              >
                <!-- how many vaults you have, at a glance -->
                <span class="w-10 h-10 flex items-center justify-center rounded-full bg-brand text-white text-lg font-semibold">{{ vaults.length }}</span>
                <p class="mt-2 font-medium text-xs leading-tight">Vaults</p>
              </router-link>
              <a
                href="#"
                class="absolute right-0 inset-y-0 flex items-center px-2 text-grey no-underline hover:text-white"
                title="Switch vault"
                @click.prevent="toggleVaults()"
              >
                <i class="fa fa-chevron-right"></i>
              </a>
            </div>

            <!-- pages of the current vault -->
            <div
              v-for="item in navItems"
              :key="item.name"
              class="w-full border-t border-grey-dark"
              v-is-authorized="{
                roles: item.permissions ? [role] : null,
                permissions: item.permissions,
              }"
            >
              <router-link
                :to="{ name: item.name, params: { vault_id: $route.params.vault_id } }"
                :class="navItemClass"
                active-class="bg-black border-brand"
                @click.native="closeAllSubMenu()"
              >
                <img :src="'/images/sidenav/' + item.icon" class="w-8" :alt="item.label" />
                <p class="mt-2 font-medium text-xs leading-tight">{{ item.label }}</p>
              </router-link>
            </div>

            <div v-if="inVault" class="w-full border-t border-b border-grey-dark">
              <a
                href="#"
                :class="[navItemClass, { 'bg-black border-brand': vault_settings_current_active || toggle_vault_settings }]"
                @click.prevent="toggleVaultSettings()"
              >
                <img src="/images/sidenav/icn_settings_grey.png" class="w-8" alt="Vault settings" />
                <p class="mt-2 font-medium text-xs leading-tight">Vault Settings</p>
              </a>
            </div>
          </div>
        </mq-layout>

        <!-- Mobile -->
        <mq-layout
          mq="mobile"
          class="bg-grey-darkest flex flex-col"
          :class="{
            'overflow-y-auto w-full fixed min-h-screen': toggle_menu_css,
          }"
        >
          <div class="flex-none text-center p-2">
            <div class="py-4 absolute">
              <hamburger
                :stroke="2"
                :gap="5"
                color="#FFF"
                :open.sync="toggle_menu"
              ></hamburger>
            </div>
            <img
              :src="'/images/header/' + vault_type + '-logo_mobile.png'"
              alt
              class="w-auto h-16 mx-auto"
            />
          </div>

          <div
            v-if="isAuthenticated"
            id="mobile-nav"
            class="bg-grey-darkest overflow-y-auto flex-1"
          >
            <transition
              name="slideLeft"
              v-on:before-enter="toggle_menu_css = true"
              v-on:before-leave="toggle_menu_css = false"
            >
              <div
                class="
                  w-full
                  bg-grey-darkest
                  z-90
                  absolute
                  border-t border-grey-dark
                "
                v-if="toggle_menu"
              >
                <a
                  href="#"
                  class="
                    text-white
                    no-underline
                    flex flex-row
                    items-center
                    hover:bg-black
                    p-6
                    px-1
                    border-t border-b border-grey-dark
                    relative
                    z-75
                  "
                  @click.prevent="toggleAccount()"
                >
                  <div class="w-1/4 flex justify-center">
                    <img
                      src="/images/sidenav/icn_account_grey.png"
                      alt
                      class="h-8"
                    />
                  </div>
                  <div class="w-3/4 flex justify-between">
                    <div class="flex items-center">
                      <div
                        class="
                          flex
                          py-1
                          px-2
                          border
                          rounded-full
                          justify-center
                          items-center
                          text-xs
                          mr-2
                        "
                        :class="{
                          'bg-white text-brand': toggle_account,
                          'border-red-light bg-red-light': !toggle_account,
                        }"
                        v-if="
                          notif_counts.unfiled +
                            notif_counts.discussions +
                            notif_counts.tasks +
                            notif_counts.events >
                          0
                        "
                      >
                        <i
                          class="fa-bell flex-none mr-1"
                          :class="{ fas: toggle_account, far: !toggle_account }"
                        ></i>
                        <span class="flex-none">{{
                          notif_counts.unfiled +
                          notif_counts.discussions +
                          notif_counts.tasks +
                          notif_counts.events
                        }}</span>
                      </div>
                      <span class="font-medium text-lg">Hi {{ fullName }}</span>
                    </div>

                    <div class="px-6 flex items-center">
                      <i class="fa fa-chevron-down" v-if="!toggle_account"></i>
                      <i
                        class="fa fa-chevron-up text-brand"
                        v-if="toggle_account"
                      ></i>
                    </div>
                  </div>
                </a>

                <div id="account" class="relative z-50 overflow-hidden">
                  <transition
                    name="slideDown"
                    enter-active-class="slideInDown"
                    leave-active-class="slideOutUp"
                  >
                    <div v-show="toggle_account">
                      <global-count-notif
                        v-model="notif_counts"
                      ></global-count-notif>
                      <div class="w-full bg-black flex flex-col items-end p-4">
                        <router-link
                          :to="{ name: 'personal-details' }"
                          class="
                            font-medium
                            text-white text-right
                            p-2
                            w-full
                            no-underline
                          "
                          @click.native="toggle_menu = !toggle_menu"
                          >Personal Details</router-link
                        >
                        <router-link
                          :to="{ name: 'change-password' }"
                          class="
                            font-medium
                            text-white text-right
                            p-2
                            w-full
                            no-underline
                          "
                          @click.native="toggle_menu = !toggle_menu"
                          >Change password</router-link
                        >
                        <button
                          class="font-medium text-white text-right p-2 w-full"
                          @click.prevent="logout"
                        >
                          <i class="fa fa-sign-out fa-lg text-brand"></i> Logout
                        </button>
                      </div>
                    </div>
                  </transition>
                </div>

                <div
                  class="
                    text-white
                    no-underline
                    flex flex-row
                    items-center
                    hover:bg-black
                    p-6
                    px-2
                    border-t border-b border-grey-dark
                    relative
                    z-75
                  "
                  :class="{
                    'bg-black': toggle_vaults,
                    'bg-black': vaults_current_active,
                  }"
                >
                  <router-link
                    :to="{ name: 'vaults' }"
                    class="w-4/5 flex flex-row text-white no-underline"
                    @click.native="toggle_menu = !toggle_menu"
                  >
                    <div class="w-1/3 flex justify-center">
                      <span
                        class="
                          w-8
                          h-8
                          text-base
                          flex flex-row
                          items-center
                          justify-center
                          rounded-full
                          bg-brand
                          text-white
                          font-semibold
                        "
                        >{{ vaults.length }}</span
                      >
                    </div>
                    <div class="w-3/4 flex items-center text-lg">Vaults</div>
                  </router-link>
                  <div class="w-1/6 pl-6 pr-2">
                    <div
                      class="text-white no-underline text-right"
                      @click.prevent="toggleVaultsMobile()"
                    >
                      <i
                        class="fa fa-chevron-down"
                        v-if="!toggle_vaults_mobile"
                      ></i>
                      <i
                        class="fa fa-chevron-up text-brand"
                        v-if="toggle_vaults_mobile"
                      ></i>
                    </div>
                  </div>
                </div>

                <div id="vault-list" class="relative z-50 overflow-hidden">
                  <transition
                    name="slideDown"
                    enter-active-class="slideInDown"
                    leave-active-class="slideOutUp"
                  >
                    <div
                      class="w-full bg-black flex flex-col items-center p-4"
                      v-if="toggle_vaults_mobile"
                    >
                      <div class="vaults w-full">
                        <div
                          v-for="(vault, index) in vaults"
                          :key="index"
                          class="
                            font-medium
                            group
                            flex flex-row
                            justify-between
                            p-2
                            border-grey-dark
                          "
                          :class="{ 'border-b': isLastVaultOnList(vault) }"
                        >
                          <div class="w-full flex justify-end">
                            <a
                              href="#"
                              class="
                                my-auto
                                text-white
                                font-medium
                                group-hover:text-brand
                                no-underline
                              "
                              @click.prevent="
                                toggle_menu = !toggle_menu;
                                toggle_vaults_mobile = !toggle_vaults_mobile;
                                selectAsCurrentVault(vault.id);
                              "
                              >{{ vault.name }}</a
                            >
                          </div>
                        </div>
                      </div>
                    </div>
                  </transition>
                </div>

                <div
                  v-if="
                    $route.params.vault_id != undefined &&
                    $authorize.isAuthorized([role], ['can_access_dashboard'])
                  "
                >
                  <router-link
                    :to="{
                      name: 'dashboard',
                      params: { vault_id: $route.params.vault_id },
                    }"
                    class="
                      text-white
                      no-underline
                      flex flex-row
                      items-center
                      hover:bg-black
                      p-6
                      px-1
                      border-t border-b border-grey-dark
                    "
                    active-class="bg-black"
                    @click.native="toggle_menu = !toggle_menu"
                  >
                    <div class="w-1/4 flex justify-center">
                      <img src="/images/sidenav/icn_dashboard_grey.png" class="w-8" />
                    </div>
                    <div class="w-3/4 flex justify-between">
                      <p class="font-medium text-lg">Dashboard</p>
                    </div>
                  </router-link>
                </div>

                <div
                  v-if="$route.params.vault_id != undefined"
                  v-is-authorized="{
                    roles: [role],
                    permissions: ['can_upload'],
                  }"
                >
                  <router-link
                    :to="{
                      name: 'upload',
                      params: { vault_id: $route.params.vault_id },
                    }"
                    class="
                      text-white
                      no-underline
                      flex flex-row
                      items-center
                      hover:bg-black
                      p-6
                      px-1
                      border-t border-b border-grey-dark
                    "
                    active-class="bg-black"
                    @click.native="toggle_menu = !toggle_menu"
                  >
                    <div class="w-1/4 flex justify-center">
                      <img src="/images/sidenav/icn_upload_grey.png" class="w-8" />
                    </div>
                    <div class="w-3/4 flex justify-between">
                      <p class="font-medium text-lg">Upload</p>
                    </div>
                  </router-link>
                </div>

                <div v-if="$route.params.vault_id != undefined">
                  <router-link
                    :to="{
                      name: 'search-documents',
                      params: { vault_id: $route.params.vault_id },
                    }"
                    class="
                      text-white
                      no-underline
                      flex flex-row
                      items-center
                      hover:bg-black
                      p-6
                      px-1
                      border-t border-b border-grey-dark
                    "
                    active-class="bg-black"
                    @click.native="toggle_menu = !toggle_menu"
                  >
                    <div class="w-1/4 flex justify-center">
                      <img src="/images/sidenav/icn_documents_grey.png" class="w-8" />
                    </div>
                    <div class="w-3/4 flex justify-between">
                      <p class="font-medium text-lg">Documents</p>
                    </div>
                  </router-link>
                </div>

                <div
                  v-if="$route.params.vault_id != undefined"
                  v-is-authorized="{
                    roles: [role],
                    permissions: ['can_view_members'],
                  }"
                >
                  <router-link
                    :to="{
                      name: 'vault-members',
                      params: { vault_id: $route.params.vault_id },
                    }"
                    class="
                      text-white
                      no-underline
                      flex flex-row
                      items-center
                      hover:bg-black
                      p-6
                      px-1
                      border-t border-b border-grey-dark
                    "
                    active-class="bg-black"
                    @click.native="toggle_menu = !toggle_menu"
                  >
                    <div class="w-1/4 flex justify-center">
                      <img src="/images/sidenav/icn_members_grey.png" class="w-8" />
                    </div>
                    <div class="w-3/4 flex justify-between">
                      <p class="font-medium text-lg">Members</p>
                    </div>
                  </router-link>
                </div>

                <div
                  v-if="$route.params.vault_id != undefined"
                  :class="{
                    'custom-bottom-padding': !toggle_vault_settings_mobile,
                  }"
                >
                  <a
                    href="#"
                    class="
                      text-white
                      no-underline
                      flex flex-row
                      items-center
                      hover:bg-black
                      p-6
                      px-1
                      border-t border-b border-grey-dark
                    "
                    :class="{ 'bg-black': toggle_vault_settings_mobile }"
                    @click.prevent="toggleVaultSettingsMobile()"
                  >
                    <div class="w-1/4 flex justify-center">
                      <img src="/images/sidenav/icn_settings_grey.png" class="w-8" />
                    </div>
                    <div class="w-3/4 flex justify-between">
                      <p class="font-medium text-lg">Vault Settings</p>
                      <div class="px-6 flex items-center">
                        <i
                          class="fa fa-chevron-down"
                          v-if="!toggle_vault_settings_mobile"
                        ></i>
                        <i
                          class="fa fa-chevron-up text-brand"
                          v-if="toggle_vault_settings_mobile"
                        ></i>
                      </div>
                    </div>
                  </a>
                </div>

                <div
                  id="vault-settings"
                  class="relative z-50 overflow-hidden h-full bg-grey-darkest"
                  v-if="toggle_vault_settings_mobile"
                >
                  <transition
                    name="slideDown"
                    enter-active-class="slideInDown"
                    leave-active-class="slideOutUp"
                  >
                    <vault-settings-nav-mobile
                      @close-settings="closeSettings"
                      v-if="toggle_vault_settings_mobile"
                    ></vault-settings-nav-mobile>
                  </transition>
                </div>
              </div>
            </transition>
          </div>
        </mq-layout>
      </div>

      <div v-else class="h-full bg-grey-darkest">
        <div
          class="
            hidden
            md:flex
            w-32
            min-h-screen
            bg-grey-darkest
            flex-col
            items-center
            py-4
          "
        >
          <a href="/#/vaults">
            <img :src="'/images/sidenav/' + vault_type + '-sidelogo.png'" alt />
          </a>
        </div>
      </div>
    </div>

    <vaults-list
      :vaults="vaults"
      :static="static"
      :toggle-vaults="toggle_vaults"
      @toggle-vaults="toggleVaultViaVaultsList"
      @select-as-current-vault="selectAsCurrentVault"
    ></vaults-list>

    <div id="vault-settings" v-if="!static">
      <transition name="slideLeft">
        <div
          class="
            w-64
            fixed
            h-full
            bg-grey-lighter
            flex flex-col
            items-start
            shadow-inner
            z-50
            border-r border-grey
          "
          v-show="toggle_vault_settings"
        >
          <button
            class="md:hidden text-black self-start pt-8 pl-3"
            @click.prevent="toggle_vault_settings = !toggle_vault_settings"
          >
            <i class="fa fa-chevron-left"></i> Back
          </button>
          <vault-settings-nav
            @close-settings="closeSettings"
            @not-available="$refs.not_available.open()"
          ></vault-settings-nav>
        </div>
      </transition>
    </div>

    <sweet-modal icon="warning" ref="not_available" overlay-theme="dark"
      >This feature is not yet available</sweet-modal
    >

    <sweet-modal
      overlay-theme="dark"
      hide-close-button
      blocking
      enable-mobile-fullscreen
      ref="t_and_c"
      :width="$mq == 'mobile' ? '100%' : '50%'"
    >
      <div class="clearfix">
        <p class="text-brand text-4xl">Terms and Conditions</p>
        <p class="font-semibold text-black">
          To use {{ vault_name }}, you must read and agree to the terms and
          conditions.
        </p>
        <div
          class="
            border border-grey
            overflow-y-scroll
            text-left
            p-3
            content
            my-2
          "
        >
          <terms-and-conditions></terms-and-conditions>
        </div>
        <div class="flex justify-between" v-if="!processing_t_and_c">
          <button
            class="px-4 py-3 font-medium bg-grey text-white rounded"
            @click.prevent="
              $refs.t_and_c.close();
              cancelTermsAndConditions();
            "
          >
            <i class="fa fa-times"></i> Cancel
          </button>

          <button
            class="
              px-4
              py-3
              font-medium
              bg-white
              text-black
              rounded
              border
              hover:bg-grey-lighter
            "
            @click.prevent="download"
          >
            <i class="fa fa-download text-brand"></i> Download T&amp;Cs
          </button>

          <button
            class="px-4 py-3 font-medium bg-brand text-white rounded"
            @click.prevent="acceptTermsAndConditions()"
          >
            <i class="fa fa-check"></i> I agree
          </button>
        </div>
        <div v-else class="text-center">
          <p class="bg-grey-ligthest p-4">
            <i class="fa fa-spin fa-spinner"></i> Please wait...
          </p>
        </div>
      </div>
      <div class="h-48 lg:h-auto"></div>
    </sweet-modal>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import vClickOutside from "v-click-outside";
import VaultsList from "./SideNav/VaultsList";
import GlobalCountNotif from "./GlobalCountNotif";
import TermsAndConditions from "./TermsAndConditions";

export default {
  components: {
    VaultsList,
    GlobalCountNotif,
    TermsAndConditions,
  },

  directives: {
    clickOutside: vClickOutside.directive,
  },

  props: {
    static: {
      default() {
        return false;
      },
    },
    showGlobalLink: {
      default() {
        return true;
      },
    },
  },

  data() {
    return {
      vaults: {},

      parent_vault_selected: false,

      toggle_vault_settings: false,
      toggle_vaults: false,

      toggle_vault_settings_mobile: false,
      toggle_vaults_mobile: false,

      toggle_menu: false,
      toggle_account: false,
      toggle_menu_css: false,

      processing_t_and_c: false,

      notif_counts: {
        discussions: 0,
        tasks: 0,
        events: 0,
        unfiled: 0,
      },
    };
  },

  mounted() {
    if (!this.static) {
      this.$ebus.$on("refresh-vaults", () => {
        this.getVaults();
        this.$ebus.$emit("refresh-unfiled");
      });

      this.$ebus.$on("get-categorized-tags", (payload) => {
        this.getCategorizedTags(payload);
      });

      this.getVaults();
      this.getRoles();
    }

    window.addEventListener("keydown", this.onKeydown);
  },

  beforeDestroy() {
    window.removeEventListener("keydown", this.onKeydown);
  },

  methods: {
    toggleVaultViaVaultsList(payload) {
      this.toggle_vaults = !payload;
    },

    toggleVaults() {
      this.toggle_vaults = !this.toggle_vaults;
      this.toggle_vault_settings = false;
    },

    toggleVaultsMobile() {
      this.toggle_vaults_mobile = !this.toggle_vaults_mobile;
    },

    toggleVaultSettings() {
      this.toggle_vaults = false;
      this.toggle_vault_settings = !this.toggle_vault_settings;
    },

    toggleVaultSettingsMobile() {
      this.toggle_vault_settings_mobile = !this.toggle_vault_settings_mobile;
    },

    toggleAccount() {
      this.toggle_account = !this.toggle_account;
    },

    closeAllSubMenu() {
      this.toggle_vault_settings = false;
      this.toggle_vaults = false;
    },

    closeSettings(payload) {
      this.toggle_vault_settings = false;
      this.toggle_vault_settings_mobile = false;
      this.toggle_menu = false;
    },

    getVaults() {
      axios.get("/api/vaults").then((resp) => {
        if (resp.data !== "") {
          this.vaults = resp.data;
          this.$store.commit("setVaults", resp.data);
          if (
            !_.isNil(this.$route.params) &&
            _.has(this.$route.params, "vault_id")
          ) {
            this.setCurrentVault();

            this.getUserRole();
          }
        }
      });
    },

    setCurrentVault(vault_id = "") {
      if (vault_id == "") {
        vault_id = this.$route.params.vault_id;
      }

      this.$store.commit("setCurrentVault", vault_id);
    },

    getUserRole() {
      if (
        this.$route.params != undefined &&
        !_.isEmpty(this.$route.params.vault_id)
      ) {
        axios
          .get(`/api/vaults/${this.$route.params.vault_id}/my-role`)
          .then(({ data }) => {
            if (!_.isEmpty(data)) {
              this.$store.commit("setUserRole", data);
              this.getCategorizedTags();
            }
          });
      }
    },

    getCategorizedTags(vault_id) {
      let id = "";
      if (_.isNil(vault_id)) {
        id = this.$route.params.vault_id;
      } else {
        id = vault_id;
      }

      axios
        .get(`/api/vaults/${id}/roles/${this.role_id}/categorized-tags`)
        .then(({ data }) => {
          this.$store.commit("setCategorizedTags", {
            tags: data,
            for_vault: this.$route.params.vault_id,
          });
        });
    },

    selectAsCurrentVault(vault_id) {
      this.setCurrentVault(vault_id);

      axios.get(`/api/vaults/${vault_id}/my-role`).then(({ data }) => {
        if (data) {
          this.$store.commit("setUserRole", data);

          this.$authorize
            .isAuthorized(
              [this.role],
              ["can_file_document", "can_tag_document"]
            )
            .then(() => {
              this.$authorize
                .isAuthorized([this.role], ["can_access_dashboard"])
                .then(() => {
                  this.$router
                    .push({
                      name: "dashboard",
                      params: { vault_id: vault_id },
                    })
                    .catch((err) => {
                      if (err.name != "NavigationDuplicated") {
                        throw err;
                      }
                    });
                  this.$ebus.$emit("get-categorized-tags");
                })
                .catch(() => {
                  this.$router
                    .push({
                      name: "search-documents",
                      params: { vault_id: vault_id },
                    })
                    .catch((err) => {
                      if (err.name != "NavigationDuplicated") {
                        throw err;
                      }
                    });
                  this.$ebus.$emit("get-categorized-tags");
                });
            })
            .catch(() => {
              this.$router
                .push({
                  name: "search-documents",
                  params: { vault_id: vault_id },
                })
                .catch((err) => {
                  if (err.name != "NavigationDuplicated") {
                    throw err;
                  }
                });
            });
        }
      });
    },

    getRoles() {
      axios.get(`/api/roles`).then(({ data }) => {
        if (!_.isEmpty(data)) {
          this.$store.commit("setRoles", data);
        }
      });
    },

    onClickOutside() {
      this.toggle_vault_settings = false;
      this.toggle_vaults = false;
      this.toggle_menu = false;
    },

    // Escape closes the vault list and the settings flyout
    onKeydown(event) {
      if (event.key === "Escape") {
        this.closeAllSubMenu();
      }
    },

    isLastVaultOnList(vault) {
      if (_.last(this.vaults).id != vault.id) {
        return true;
      }

      return false;
    },

    logout() {
      axios
        .post("/api/logout")
        .then((resp) => {
          this.$store.commit("setUser", {});
          // this.$store.commit('authenticate', false);
          this.$store.commit("setVaults", []);
          this.$auth.logout();
          window.location.href = "/auth-login";
        })
        .catch(function (error) {
          if (error.response) {
            console.log(error.response.data);
            console.log(error.response.status);
            console.log(error.response.headers);
          }
        });
    },

    acceptTermsAndConditions() {
      this.processing_t_and_c = true;
      axios.patch("/api/users/accept-tc").then(({ data }) => {
        if (data.success) {
          this.processing_t_and_c = false;
          this.$refs.t_and_c.close();
        }
      });
    },

    cancelTermsAndConditions() {
      this.processing_t_and_c = false;
      axios.post("/api/logout").then(({ data }) => {
        window.location.href = "/auth-login";
      });
    },

    download() {
      axios
        .get("/download-t-and-c", { responseType: "blob" })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Terms and Conditions.pdf"); //or any other extension
          document.body.appendChild(link);
          link.click();
        });
    },
  },

  computed: {
    ...mapGetters({
      isAuthenticated: "isAuthenticated",
      getCurrentVault: "getCurrentVault",
      role: "getUserRole",
      fullName: "getUserFullName",
      role_id: "getUserRoleId",
      categorized_tags: "getCategorizedTags",
      user: "getUser",
    }),

    vaults_current_active() {
      let vaults_active_pages = ["tagging", "recently-filed", "vaults"];
      if (_.includes(vaults_active_pages, this.$route.name)) {
        return true;
      }
    },

    // every page of the settings flyout lives under /vault-settings/
    vault_settings_current_active() {
      return _.startsWith(this.$route.path, "/vault-settings/");
    },

    vault_name() {
      return _.startCase(window.vault_type) + "Vault";
    },
    inVault() {
      return this.$route.params.vault_id != undefined;
    },
    // one look for every rail item: 80px tall, brand bar on the left when active
    navItemClass() {
      return "w-full h-20 flex flex-col items-center justify-center px-1 text-white no-underline border-l-4 border-transparent hover:bg-black";
    },
    navItems() {
      if (!this.inVault) {
        return [];
      }
      return [
        { name: "dashboard", label: "Dashboard", icon: "icn_dashboard_grey.png", permissions: ["can_access_dashboard"] },
        { name: "upload", label: "Upload", icon: "icn_upload_grey.png", permissions: ["can_upload"] },
        { name: "search-documents", label: "Documents", icon: "icn_documents_grey.png", permissions: null },
        { name: "vault-members", label: "Members", icon: "icn_members_grey.png", permissions: ["can_view_members"] },
      ];
    },

    vault_type() {
      return window.vault_type;
    },
  },

  watch: {
    user() {
      if (!_.isNil(this.user.id) && !this.user.accepted_tc) {
        this.$refs.t_and_c.open();
      }
    },

    // The current vault and the user's role used to be fetched only on mount.
    // Typing a vault URL from the vault list, or browser back/forward between
    // vaults, changes the route without remounting the sidebar, so the role
    // stayed empty and every v-is-authorized element stayed hidden. Vault
    // cards set the current vault before pushing the route, so this only
    // fires when the route reaches a vault nothing else has selected.
    "$route.params.vault_id"(vault_id) {
      if (_.isEmpty(vault_id) || vault_id == this.$store.getters.getCurrentVault.id) {
        return;
      }
      if (_.isEmpty(this.$store.getters.getVaults)) {
        return; // getVaults() is still running and reads the route when it lands
      }
      this.setCurrentVault(vault_id);
      this.getUserRole();
    },
  },
};
</script>

<style>
/* current vault in the rail: at most three lines, the title attribute carries the rest */
.sidenav-vault-name {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.bg-vault-count {
  background: url("/images/sidenav/icn_vaults_grey.png") no-repeat;
  background-size: contain;
}
.hamburger-button {
  width: 40px !important;
}
.content {
  height: 380px;
}
.clearfix {
  clear: both;
}
.custom-bottom-padding {
  padding-bottom: 100px;
}
</style>
